/* eslint-disable @typescript-eslint/naming-convention */
import {
    AddressByGoogle,
    BooleanTristateValue,
    ConfigurationAlert,
    JsonReply,
    NextLocator,
    Partaker,
    SelectOption,
    SelectOptionGroup,
} from '@grupo-constant/next-dtype/types';
import { PlanningArea, ServiceUserAssignment, WorkDayEvent } from '../services/services.types';
import { NextTaskTypeGoalDeliveryMode, TaskTypeTypology } from '../../../core/task-type/task-type.types';
import { LocaleString } from '../../../core/translate/localeString.model';
import { Message } from '../../../layout/common/messages/messages.types';

export interface Task {
    taskId?: string;
    parentTaskId?: string;
    belongsTo?: string;
    dataQuality?: boolean;
    taskTypeId?: string;
    taskTypeUri?: string;
    taskTypeName?: string;
    externalTaskId?: string;
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    serviceId?: string;
    marca_name?: string;
    cliente_name?: string;
    project_name?: string;
    service_name?: string;
    testTask?: boolean;
    taskTypeTypology?: TaskTypeTypology;
    /* PLANNING ZONE */
    planningAreaId?: string;
    planningAreaUri?: string;
    planningAreaName?: string;
    planningAreaTimeZoneId?: string;
    /* WHO DOES THIS TASK */
    zombieName?: string;
    zombieNumber?: number;
    companyId?: string;
    userId?: string;
    userSAMAccountName?: string;
    /* WHERE */
    addressOid?: string;
    addressUri?: string;
    addressName?: string;
    addressVicinity?: string;
    addressPlaceId?: string;
    finishedWithoutGeoposition?: string;
    fullAddress?: string;
    startedWithoutGeoposition?: string;
    addressIsDirty?: boolean;
    /* INITIATOR */
    originatorCompanyId?: string;
    originatorUserId?: string;
    originatorUserSAMAccountName?: string;
    /* WHEN */
    scheduled_from_date?: string;
    scheduled_to_date?: string;
    scheduled_from_time?: string;
    scheduled_to_time?: string;
    scheduled_from_time2?: string;
    scheduled_to_time2?: string;
    startTaskDate?: string;
    endTaskDate?: string;
    delayedStart?: boolean;
    delayedEnd?: boolean;
    startTaskDate2?: string;
    endTaskDate2?: string;
    delayedStart2?: boolean;
    delayedEnd2?: boolean;
    minutesBySchedule?: number;
    minutesByWorkDayType?: number;
    minutesReal?: number;
    /* STATUS */
    status?: TaskStatusEnum;
    timestamp?: string;
    trackingEvent?: TaskTrackingEventEnum;
    activationDate?: string;
    cancellationReasonDescription?: LocaleString;
    rejectedReasonDescription?: LocaleString;
    /* SENT INFO */
    messageId?: string;
    notice?: any;
    noticeUri?: string;
    serverPayload?: TaskPayload;
    /* WORKDAY */
    workDayTypeUri?: string;
    workDayTypeOid?: string;
    workDayTypeCode?: string;
    workDayTypeDescription?: LocaleString;
    workDayTypeMode?: TaskWorkDayTypeMode;
    /* ALERT */
    alert?: ConfigurationAlert[];
    /*COPY*/
    copy?: boolean;
    copyCantEdit?: boolean;
    oldTaskId?: string;
    /* OTHER */
    startEndDifferenceMinutes?: number;
    delayedStartMinutes?: number;
    delayedEndMinutes?: number;
    timeRemainingToStart?: number;
    timeRemainingToEnd?: number;
    delayedStartMinutes2?: number;
    delayedEndMinutes2?: number;
    timeRemainingToStart2?: number;
    timeRemainingToEnd2?: number;
    workingTime?: number;
    timeFromStart?: number;
}

export interface TaskSearchTask {
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    marca_name?: string;
    cliente_name?: string;
    project_name?: string;
    service_name?: string;
    serviceId?: string;
    belongsTo?: string;
    dataQuality?: boolean;
    taskTypeUri?: string;
    taskId?: string;
    parentTaskId?: string;
    externalTaskId?: string;
    taskTypeId?: string;
    taskTypeName?: string;
    taskTypeTypology?: TaskTypeTypology;
}
export interface GroupSearch {
    planningAreaId?: string;
    planningAreaUri?: string;
    planningAreaName?: string;
    planningAreaTimeZoneId?: string;
}

// falta añadir las propiedades timeFrom_split_workday y timeTo_split_workday
export interface ScheduleSearch {
    from?: string;
    to?: string;
    timeFrom?: string;
    timeFrom2?: string;
    timeTo?: string;
    timeTo2?: string;
    fromDateVisibility?: string;
    endDateVisibility?: string;
    workDayTypeOid?: string;
    workDayTypeUri?: string;
    workDayTypeCode?: string;
    workDayTypeDescription?: LocaleString;
    workDayTypeMode?: string;
    startTaskDate?: string;
    endTaskDate?: string;
    startTaskDate2?: string;
    endTaskDate2?: string;
    delayedStart?: boolean;
    delayedEnd?: boolean;
    delayedStart2?: boolean;
    delayedEnd2?: boolean;
    minutesBySchedule?: number;
    minutesByWorkDayType?: number;
    minutesReal?: number;
    taskAssignmentSummary?: TaskAssignmentSummary[];
}

export interface WhoSearch {
    zombieNumber?: number;
    zombieName?: string;
    userId?: string;
    userName?: string;
    userSAMAccountName?: string;
}

export interface WhereSearch {
    addressOid?: string;
    addressUri?: string;
    addressName?: string;
    addressVicinity?: string;
    addressPlaceId?: string;
}

export interface AuditSearch {
    originatorUserId?: string;
    originatorCompanyId?: string;
    originatorUserSAMAccountName?: string;
}

export interface StatusSearch {
    status: TaskStatusEnum;
    customStatus?: CustomTaskStatusEnum;
    timestamp: string;
    trackingEvent?: TaskTrackingEventEnum;
    activationDate?: string;
    cancellationReason?: LocaleString;
    rejectedReason?: LocaleString;
    startedWithoutGeoposition?: BooleanTristateValue;
    finishedWithoutGeoposition?: BooleanTristateValue;
}

export interface NoticeSearch {
    notice?: any;
    noticeUri?: string;
}

export interface TaskSearch {
    task?: TaskSearchTask;
    group?: GroupSearch;
    schedule?: ScheduleSearch;
    who?: WhoSearch;
    where?: WhereSearch;
    audit?: AuditSearch;
    status?: StatusSearch;
    notice?: NoticeSearch;
    payload?: TaskPayload;
    withNoticeData?: boolean;
    alert?: ConfigurationAlert[];
    action?: TaskMenuActionEnum[];
    showInfo?: boolean;
    copy?: boolean;
    copyCantEdit?: boolean;
    oldTaskId?: string;
    checked?: boolean;
    showReason?: boolean;
    startEndDifferenceMinutes?: number;
    delayedStartMinutes?: number;
    delayedEndMinutes?: number;
    timeRemainingToStart?: number;
    timeRemainingToEnd?: number;
    delayedStartMinutes2?: number;
    delayedEndMinutes2?: number;
    timeRemainingToStart2?: number;
    timeRemainingToEnd2?: number;
    workingTime?: number;
    timeFromStart?: number;
}

export interface TaskPayload {
    service: {
        serviceId: string;
        name: string;
        dateFrom: string;
        dateTo: string;
    };
    taskId: string;
    taskTypeUri: string;
    taskTypeId: string;
    externalTaskId: string;
    addressOid: string;
    addressUri: string;
    addressName: string;
    addressVicinity: string;
    addressExternalCode: string;
    address: AddressByGoogle;
    planningAreaId: string;
    planningAreaUri: string;
    schedule?: {
        from: string;
        timeFrom: string;
        timeTo: string;
        workDayType: WorkDay;
    };
    goalStatus?: TaskGoalStatus;
    notice?: any;
    zombieName: string;
    zombieNumber: number;
    extraPayload?: any;
}

export interface RetrieveNextTaskCustomGoalAutoAssignmentRequest {
    taskTypeUri: string;
    task_scheduled_from_date: string;
    addressOid: string;
    workdayTypeOid?: string;
    task_scheduled_from_time?: string;
    task_scheduled_to_time?: string;
    task_scheduled_from_time2?: string;
    task_scheduled_to_time2?: string;
}

export interface RetrieveNextTaskCustomGoalAutoAssignmentReply extends JsonReply {
    goal: {
        [key: string]: number;
    };
}

export interface CheckDataQualityTaskRequest {
    serviceId: string;
    taskId: string[];
    uncheck: boolean;
}

export interface TaskGoal {
    goalName?: string;
    goalValue?: number;
    masterName?: string;
    masterOid?: string;
    order?: number;
    taskId?: string;
    valuePercent?: number;
    valueInUnits?: number;
}

export interface TaskGoalStatus {
    informative: boolean;
    row: {
        concept: string;
        caption: LocaleString;
        goal: number;
        goalDeliveryMode: NextTaskTypeGoalDeliveryMode;
        goalUnitOfMeasurement: LocaleString;
        percent: number;
        valueInUnits: number;
        valuePercent: number;
    }[];
}

export interface TaskAssignmentSummary {
    taskTypeName?: string;
    taskTypeUri?: string;
    count?: number;
    minutes?: number;
}

export interface TaskCalculatedTime {
    tasks: number;
    minutes: number;
    display?: string;
}

export interface CancelReason {
    description: {
        'es-ES': string;
    };
    code: string;
    _oid: string;
    _deprecated: boolean;
}

export interface Pagination {
    length: number;
    size: number;
    page: number;
    lastPage: number;
    startIndex: number;
    endIndex: number;
}

export interface OrderedAlert {
    type?: string;
    alerts?: LocaleString[];
}

export interface WorkDay {
    code?: string;
    description?: LocaleString[];
    duration?: number;
    from?: string;
    from2?: string;
    to?: string;
    to2?: string;
    mode?: TaskWorkDayTypeMode;
    _oid?: string;
    weekDays: string;
    _deprecated?: boolean;
}

export interface RetrieveTaskBody {
    locator: NextLocator;
}

export interface RetrieveTaskReply extends JsonReply {
    task: Task[];
    partaker?: Partaker;
}

export interface SearchTaskBody {
    locator: NextLocator;
    planningArea?: any[];
    handwrittenId?: any[];
    userId?: string;
    zombie?: ZombieNumberCompany;
    alias?: string;
    taskTypeId?: any[];
    address?: string;
    from?: string;
    to?: string;
    from_time?: string;
    to_time?: string;
    statuses?: any[];
    reason?: any[];

    chunksize?: number;
    lastConsumedTaskID?: string;
    count: boolean;

    orderBy?: string;
    descending?: boolean;

	isGroupedByUser?: boolean;
	lastConsumedUserId?: string;
}
export interface SearchTaskReply extends JsonReply {
    task?: TaskSearch[];
    taskByUser?: SearchUserTask[];
    partaker?: Partaker;
    count?: number;
    eof?: boolean;
}

export interface SearchUserTask {
    userId: string;
    displayName: string;
    sAMAccountName: string;
    taskCount: number;
    zombieName: string;
    START_WORKDAY?: string;
    PAUSE_WORKDAY?: string;
    RESTART_WORKDAY?: string;
    END_WORKDAY?: string;
    taskList: TaskSearch[];
    workDayEventList?: WorkDayEvent[];
}

export interface FlushTaskBody {
    taskId?: string;
    serviceId: string;
    taskTypeUri: string;
    taskTypeId: string;
    externalTaskId?: string;
    planningAreaId: string;
    zombieNumber?: number;
    userId?: string;
    addressOid?: string;
    addressUri?: string;
    scheduled_from_date: string;
    scheduled_to_date?: string;
    scheduled_from_time?: string;
    scheduled_to_time?: string;
    scheduled_from_time2?: string;
    scheduled_to_time2?: string;
    notice?: any;
    changeUserZombieLink?: boolean;
    workDayTypeOid?: string;
    goals: TaskGoal[];
    extraPayload?: any;
}

export interface FlushTaskReply extends JsonReply {
    task: Task;
    row: TaskSearch;
    partaker?: Partaker;
}

export interface FlushTaskMultipleUsersBody {
    serviceId: string;
    taskTypeId: string;
    externalTaskId?: string;
    userIdsByPlanningArea: { [area: string]: string[] };
    addressOid?: string;
    addressUri?: string;
    scheduled_from_date: string;
    scheduled_to_date?: string;
    scheduled_from_time?: string;
    scheduled_to_time?: string;
    scheduled_from_time2?: string;
    scheduled_to_time2?: string;
    notice?: any;
    workDayTypeOid?: string;
    goals: TaskGoal[];
    extraPayload?: any;
}

export interface FlushTaskMultipleUsersReply extends JsonReply {
    tasks: Task[];
    rows: TaskSearch[];
    partaker?: Partaker;
}

export interface FlushBeautifyTask {
    taskId: string;
    serviceId: string;
    externalTaskId?: string;
    zombieNumber?: number;
    addressOid?: string;
    scheduled_from_date: string;
    scheduled_to_date?: string;
    scheduled_from_time?: string;
    scheduled_to_time?: string;
    scheduled_from_time2?: string;
    scheduled_to_time2?: string;
    startTaskDate?: string;
	endTaskDate?: string;
	startTaskDate2?: string;
	endTaskDate2?: string;
    workDayTypeOid?: string;
    workDayTypeUri?: string;
    taskTypeId?: string;
}

export interface RetrieveTaskSearchOptionsBody {
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    serviceId?: string;
}

export interface RetrieveTaskSearchOptionsReply extends JsonReply {
    availableSearchOptions: TaskSearchOptionsFilter[];
    planningArea?: PlanningAreaAsOption[];
    taskType?: SelectOptionGroup[];
    taskTypeForPlanning?: SelectOptionGroup[];
    address?: FilterAddress;
    schedule?: FilterDate;
    statuses?: SelectOption[];
    reason?: SelectOption[];
    customer?: SelectOption[];
    project?: SelectOption[];
}

export interface FilterAddress {
    masterLocationUri: SelectOption[];
    taskType: { [uri: string]: string };
}

export interface FilterDate {
    from: string;
    to: string;
}

export interface PlanningAreaAsOption {
    code: string;
    value: LocaleString;
    _deprecated: boolean;
    export: any;
}

export enum TaskStatusEnum {
    WITHERROR = 'WITHERROR',
    PLANNED = 'PLANNED',
    SENT = 'SENT',
    ACK = 'ACK',
    RC = 'RC',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    MOVED = 'WITHEMOVEDRROR',
    MOVING = 'MOVING',
    PAUSED = 'PAUSED',
    WORKING = 'WORKING',
    PREVIEW = 'PREVIEW',
    DONE = 'DONE',
    DONE_WITH_ERROR = 'DONE_WITH_ERROR',
    SUSPENDED = 'SUSPENDED'
}

export enum CustomTaskStatusEnum {
    DOC_SENT = 'DOC_SENT',
    DOC_APPROVED = 'DOC_APPROVED',
    DOC_REJECTED = 'DOC_REJECTED'
}

export enum TaskTrackingEventEnum {
    AUTO_START = 'AUTO_START',
    WITHERROR = 'WITHERROR',
    PLANNED = 'PLANNED',
    SENT = 'SENT',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    WORKING = 'WORKING',
    PAUSED = 'PAUSED',
    DONE = 'DONE',
    DONE_WITH_ERROR = 'DONE_WITH_ERROR',
    FORM_RECEIVED = 'FORM_RECEIVED',
    RESET = 'RESET',
    DELAYED_START = 'DELAYED_START',
    DELAYED_END = 'DELAYED_END',
    DELAYED_START2 = 'DELAYED_START2',
    DELAYED_END2 = 'DELAYED_END2',
    MANUAL_SENT = 'MANUAL_SENT',
    SMS_SENT_START = 'SMS_SENT_START',
    SMS_SENT_END = 'SMS_SENT_END',
    SENT_ACK = 'SENT_ACK',
    SENT_RC = 'SENT_RC',
    UPDATED = 'UPDATED',
    MESSAGE = 'MESSAGE',
    SUSPENDED = 'SUSPENDED'

}

export enum TaskSearchOptionsFilter {
    PLANNING_AREA = 'PLANNING_AREA',
    TASK_ID = 'TASK_ID',
    SEARCH_COMPANY_USER_DN = 'SEARCH_COMPANY_USER_DN',
    TASK_TYPE = 'TASK_TYPE',
    ADDRESS = 'ADDRESS',
    DATE_RANGE = 'DATE_RANGE',
    HOUR_RANGE = 'HOUR_RANGE',
    STATUS = 'STATUS',
    REASON = 'REASON'
}

export enum TaskMenuActionEnum {
    CANCEL = 'CANCEL',
    SEND = 'SEND',
    RESET = 'RESET',
    DELETE = 'DELETE',
    START = 'START',
    PAUSE = 'PAUSE',
    FINISH = 'FINISH',
    DATA_QUALITY = 'DATA_QUALITY',
    NO_DATA_QUALITY = 'NO_DATA_QUALITY'
}

export enum TaskSearchOptionsReasonEnum {
    DELAYED_START = 'DELAYED_START',
    DELAYED_END = 'DELAYED_END',
    DELAYED_START2 = 'DELAYED_START2',
    DELAYED_END2 = 'DELAYED_END2',
    STARTED_WITHOUT_GEOPOSITION = 'STARTED_WITHOUT_GEOPOSITION',
    FINISHED_WITHOUT_GEOPOSITION = 'FINISHED_WITHOUT_GEOPOSITION',
    WITHOUT_ASSIGNED_RESOURCE = 'WITHOUT_ASSIGNED_RESOURCE',
    WITHOUT_ASSIGNED_PERSON = 'WITHOUT_ASSIGNED_PERSON',
    WITHOUT_ASSIGNED_ADDRESS = 'WITHOUT_ASSIGNED_ADDRESS',
    WITHOUT_ASSIGNED_HORARY = 'WITHOUT_ASSIGNED_HORARY',
}

export enum TaskWorkDayTypeMode {
    specificSchefule = 'SPECIFIC_SCHEDULE',
    minutes = 'MINUTES',
    split = 'SPLIT',
    none = 'NONE'
}

// new retrieveUserCalendar and retrieveZombieCalendar
export interface RetrieveTaskAssignmentsBody {
    serviceId: string;
    planningAreaId?: string;
    taskDate: string;
}

export interface RetrieveTaskAssignmentsReply {
    [key: string]: UserZombies;
}

export interface RetrieveUserCalendarBody {
    userId: string;
    planningAreaId: string;
    serviceId: string;
    date: string;
}

export interface RetrieveZombieCalendarBody {
    serviceId: string;
    date: string;
    planningArea: string;
    zombieNumber: number;

}

export interface RetrieveUserCalendarReply extends JsonReply {
    calendar: UserCalendar[];
    isAbsence: boolean;
    isHoliday: boolean;
    fromTime?: string;
    toTime?: string;
}

export interface RetrieveZombieCalendarReply extends JsonReply {
    calendar: UserCalendar[];
}


export interface UserZombies {
    users?: UserData[];
    zombies?: ZombieData[];
}

export interface UserData {
    fullname?: string;
    hrid?: string;
    trained?: boolean;
    userId?: string;
    zombieNumber?: number;
    calendar?: RetrieveUserCalendarReply;
    virtual?: boolean;
}

export interface UserDataView extends UserData {
    hide?: boolean;
}

export interface ZombieData {
    name?: string;
    userId?: string;
    zombieNumber?: number;
    calendar?: UserCalendar[];
}

export interface ZombieDataView extends ZombieData {
    hide?: boolean;
}

export interface UserCalendar {
    date?: string;
    fromTime?: string;
    numberOfTasks?: number;
    serviceId?: string;
    taskTypeName?: string;
    toTime?: string;
}

export interface CompanyDataUser {
    userId?: string;
    userPrincipalName?: string;
    cn?: string;
    sAMAccountName?: string;
    displayName?: string;
    active?: boolean;
    telephoneNumber?: string;
    distinguishedName?: string;
    profileImage?: string;
}

export interface ZombieNumberCompany {
    name: string;
    // eslint-disable-next-line id-blacklist
    number: number;
    planningArea: PlanningArea;
}

export interface TaskEventSummary {
    eventSummary: EventSummary[];
}

export interface TaskTracking {
    eventSummary: EventSummary[];
}

export interface EventSummary {
    event: string;
    timestamp: string;
    ack: string;
    rc: string;
    userId: string;
    userName: string;
    payload: InfoMessage;
    showInfo?: boolean;
}

export interface GeopositionSummary {
    event?: string;
    geopositionTimestamp?: string;
    latitude?: number;
    longitude?: number;
    userId?: string;
    userName?: string;
}

export interface GeopositionMarkerData {
    geopositionTimestamp?: string;
    latitude?: number;
    longitude?: number;
}

export interface WorkdayGeopositionMarkerData extends GeopositionMarkerData {
    event?: string;
}

export interface WorkdayReferenceGeopositionMarkerData extends GeopositionMarkerData {
    name?: string;
}

export interface InfoMessage {
    title: LocaleString;
    message: LocaleString;
}

export interface DeviceInfo {
    platformVersion?: string;
    model?: string;
    manufacturer?: string;
}

export interface UserDeviceInfo {
    appVersion?: string;
    deviceType?: string;
    deviceInfo?: DeviceInfo;
}

export interface TaskFormSummary {
    formSummary: FormSummary[];
}

export interface FormSummary {
    formUri: string;
    caption: LocaleString;
    oid: string;
    creationDate: string;
    queueDate: string;
    receivedDate: string;
}

export interface TaskOutputFile {
    serviceId: string;
    taskId: string;
    taskTypeUri: string;
    oid: string;
    title: string;
    docUrl: string;
    creationTime: string;
    sendTime: string;
    receivedTime: string;
    status: string;
    score: string;
    recipientMail: string;
    recipientRemark: string;
    historyPayload: any;
    action: {
        send: {
            caption: LocaleString;
            confirm: LocaleString;
        };
        generate: {
            caption: LocaleString;
        };
    };
}

export interface RetrieveTaskByIdBody {
    taskId: string;
    serviceId: string;
    includeTaskEventSummary?: boolean;
    includeTaskFormSummary?: boolean;
    includeTaskOutputFile?: boolean;
}

export interface RetrieveTaskByIdReply extends JsonReply {
    task: Task;
    taskEventSummary?: EventSummary[];
    taskTracking?: EventSummary[];
    taskFormSummary?: FormSummary[];
    taskOutputFile?: TaskOutputFile[];
    row?: TaskSearch;
    userLastDevice?: UserDeviceInfo;
    taskGeopositionSummary?: GeopositionSummary[];
    partaker?: Partaker;
}

export interface SendTaskListBody {
    taskId: string[];
    serviceId: string;
}

export interface SendTaskListReply extends JsonReply {
    unchangedTaskId?: string[];
}

export interface CancelTaskListBody {
    taskId: string[];
    serviceId: string;
    cancellationReason: string;

}

export interface CancelTaskListReply extends JsonReply {
    unchangedTaskId?: string[];
}

export interface ResetTaskListBody {
    taskId: string[];
    serviceId: string;
}

export interface ResetTaskListReply extends JsonReply {
    unchangedTaskId?: string[];
}

export interface RemoveTaskBody {
    taskId: string;
    serviceId: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RemoveTaskReply extends JsonReply {}

export interface DeleteTaskListRequest {
    taskId: string[];
    serviceId: string;
}

export interface DeleteTaskListReply extends JsonReply {
    notDeletedTaskId: string[];
}

export interface RetrieveMultiTaskEditBody {
    serviceId: string;
    taskId: string[];
}

export interface FlushServiceUserAssignmentBody {
    serviceId: string;
    planningArea: string;
    hrid?: string;
    trained: boolean;
}

export interface FlushServiceUserAssignmentReply extends JsonReply {
    serviceUserAssignment?: ServiceUserAssignment;
}

export interface ChangeServiceUserAssignmentTrainedBody {
    serviceId: string;
    planningArea: string;
    userId: string;
    trained: boolean;
}

export interface UserAndZombieOptions {
    code: string;
    isDefault: boolean;
    value: LocaleString;
    _deprecated: boolean;
}

export interface UserMultiEdit {
    userId: string;
    name: string;
    options: UserAndZombieOptions[];
    diffNumber: number;
}

export interface ZombieMultiEdit {
    zombieNumber: number;
    zombieName: string;
    options: UserAndZombieOptions[];
    diffNumber: number;
}

export interface RetrieveMultiTaskEditReply extends JsonReply {
    planningArea: { planningAreaOid?: string; options: SelectOption[]; diffNumber: number };
    scheduled_from_date: { diffNumber: number; scheduled_from_date?: string };
    scheduled_to_date: { diffNumber: number; scheduled_to_date?: string };
    scheduled_from_time: { diffNumber: number; scheduled_from_time?: string };
    scheduled_to_time: { diffNumber: number; scheduled_to_time?: string };
    scheduled_from_time2: { diffNumber: number; scheduled_from_time2?: string };
    scheduled_to_time2: { diffNumber: number; scheduled_to_time2?: string };
    user: UserMultiEdit;
    zombie: ZombieMultiEdit;
    address: { addressOid?: string; addressUri?: string; addressName?: string; addressVicinity?: string; addressPlaceId?: string; diffNumber?: number };
    workDayType: MultiTaskEditWorkDay;
    effectiveTaskId: string[];
    includeAllTask: boolean;
}

export interface MultiTaskEditWorkDay {
    workDayTypeOid: string;
    workDayTypeUri: string;
    workDayTypeCode: string;
    workDayTypeMode: TaskWorkDayTypeMode;
    options: SelectOption[];
    diffNumber: number;
}

export interface FlushMultiTaskEditBody {
    serviceId: string;
    taskId: string[];
    planningArea: string;
    scheduled_from_date: string;
    scheduled_to_date: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    scheduled_from_time2: string;
    scheduled_to_time2: string;
    userId: string;
    zombieNumber?: number;
    addressOid: string;
    addressUri: string;
    workDayTypeOid?: string;
    workDayTypeUri?: string;
    updateFieldName: string[];
    changeUserZombieLink: boolean;
}

export interface FlushMultiTaskEditReply extends JsonReply {
    effectiveTaskId: string[];
    includeAllTask: boolean;
}

export interface MultiTaskEditChecks {
    planningArea: boolean;
    scheduled_from_date: boolean;
    scheduled_to_date: boolean;
    workDayTypeOid: boolean;
    scheduled_from_time: boolean;
    scheduled_to_time: boolean;
    scheduled_from_time2: boolean;
    scheduled_to_time2: boolean;
    userId: boolean;
    zombieNumber: boolean;
    location: boolean;
}

export interface FormPropertyPrint {
    label: string;
    value: string;
}

export interface FormBaseColumns {
    _serviceId?: FormPropertyPrint;
    _taskId?: FormPropertyPrint;
    _taskTypeUri?: FormPropertyPrint;
    _oid?: FormPropertyPrint;
    _formUri?: FormPropertyPrint;
    _creationDate?: FormPropertyPrint;
    _formRelease?: FormPropertyPrint;
    _queueDate?: FormPropertyPrint;
    _userId?: FormPropertyPrint;
    _receivedDate?: FormPropertyPrint;
    _lastUpdate?: FormPropertyPrint;
    _lastUpdateUserId?: FormPropertyPrint;
}

export interface DownloadSearchTaskBody {
    searchTask: SearchTaskBody;
    language?: string;
}

export interface SendMessageBody {
    taskId: string;
    serviceId: string;
    notification: Message;
}

export interface DownloadTaskModelBody {
    serviceId: string;
    objectivesDate: string;
    language?: string;
}

export interface UploadTaskExcelBody {
    excelType: string;
    locator: NextLocator;
    async: boolean;
    file: string;
}

export interface UploadTaskModelResponse extends JsonReply {
    errors?: boolean;
    async?: boolean;
    forcedAsync?: boolean;
    file?: string;
}

export interface StartEndBody {
    serviceId: string;
    taskId: string;
    start: boolean;
    pause: boolean;
}

export interface StartEndReply {
    status?: number;
}

export interface RetrieveTaskGoalReply extends JsonReply {
    goals: TaskGoal[];
}

export interface FlushTaskGoalBody {
    taskId: string;
    serviceId: string;
    goals: TaskGoal[];
}

export interface FlushTaskGoalReply extends JsonReply {
    goalStatus: TaskGoalStatus;
}

export interface ExecuteTaskCustomActionRequest {
    taskId: string;
    serviceId: string;
    payload: {
        action: 'send' | 'generate'
    };
}
